import React from "react";
import { Row, Button } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Add } from "@mui/icons-material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import QuotationLog from "./QuotationLog";
import QuotationDraft from "./QuotationDraft";
import PageContainer, {
  defaultInnerStyle,
} from "Common/Components/PageContainer/PageContainer";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import { MecordColors } from "Constants/Color";
import { FileText, FileSignature, BadgeCheck, PhoneCall } from "lucide-react";
import { clearAllTableStates } from "utils/clearTableStates";
import { app_modules } from "helpers/apps_modules_list";
import { usePermission } from "hooks/usePermission";
import { fetchAllProductQuotations } from "slices/quotation";
import { QUOTATION_STAGE } from "Constants/StatusConstant";

// Internal tabs component that uses DynamicTabs styling but handles URL-based navigation
interface TabItem {
  value: string;
  label: string;
}

interface InternalDynamicTabsProps {
  activeTab: string;
  tabs: TabItem[];
  onTabChange: (tabValue: string) => void;
}

const InternalDynamicTabs: React.FC<InternalDynamicTabsProps> = ({
  activeTab,
  tabs,
  onTabChange,
}) => {
  // Convert activeTab string → index for smooth animation
  const activeIndex = tabs.findIndex(
    (t) => t.value.toLowerCase() === activeTab?.toLowerCase()
  );

  const handleChange = (event: React.SyntheticEvent, newIndex: number) => {
    const newTab = tabs[newIndex];
    onTabChange(newTab.value);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={activeIndex >= 0 ? activeIndex : 0}
        onChange={handleChange}
        sx={{
          "& .MuiTab-root": {
            fontFamily: "'Poppins', sans-serif",
            textTransform: "none",
            color: "#555555", // inactive dark grey
            transition: "color 0.2s ease",
          },

          "& .MuiTab-root:hover": {
            color: "#d2363a", // hover color
          },

          "& .Mui-selected": {
            color: "#d2363a !important", // active tab color
          },

          "& .MuiTabs-indicator": {
            backgroundColor: "#d2363a", // slider color
            height: "3px",
            transition: "all 0.3s ease",
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            sx={{ fontFamily: "'Poppins', sans-serif" }}
          />
        ))}
      </Tabs>

      <Divider sx={{ mt: 0, borderColor: "#c35f61" }} />
    </Box>
  );
};

interface ProductQuotationContainerProps {
  noWrapper?: boolean;
}

const QuotationContainer: React.FC<ProductQuotationContainerProps> = ({ noWrapper = false }) => {
  document.title = "Sales Quotation";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const { tab, tags: tagsParam } = useParams<{ tab?: string; tags?: string }>();
  const isTagsSearchRoute = location.pathname.startsWith("/tags-search");
  const tagsSearchQuotationStorageKey = "TagsSearchQuotationTab_product";
  const tagFromUrl = React.useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);

  const { canCreate } = usePermission(
    app_modules?.quotation?.app_name,
    app_modules?.quotation?.modules?.product
  );
  
  const [tagsQuotationTab, setTagsQuotationTab] = React.useState<string>(() => {
    const saved = sessionStorage.getItem(tagsSearchQuotationStorageKey);
    return saved === "draft" ? "draft" : "log";
  });
  const [tagsTabSummary, setTagsTabSummary] = React.useState({
    loading: false,
    logCount: 0,
    draftCount: 0,
    hasError: false,
  });
  const activeTab = isTagsSearchRoute ? tagsQuotationTab : tab || "log";

  const handleCreateQuotation = () => {
    navigate("/quotation/product/add");
  };

  // Static Dummy Data for Analytics
  const statuses = [
    {
      label: "Total Quotations",
      icon: <FileText size={18} />, // 📄 All quotation documents
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      count: 120,
      subLabel: "Total quotations created",
    },
    {
      label: "Active Quotations",
      icon: <FileSignature size={18} />, // ✍️ Active / editable quotation
      bgColor: "bg-red-50",
      textColor: "text-red-600",
      count: 35,
      subLabel: "Currently active",
    },
    {
      label: "Converted to SO",
      icon: <BadgeCheck size={18} />, // 🟢 Converted to Sales Order
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-600",
      count: 50,
      subLabel: "Successfully converted",
    },
    {
      label: "Pending Follow-ups",
      icon: <PhoneCall size={18} />, // 📞 Follow-up needed
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      count: 82,
      subLabel: "Follow-up required",
    },
  ];

  const tabs: TabItem[] = [
    { value: "log", label: "Quotation Log" },
    { value: "draft", label: "Draft" },
  ];

  const handleTabChange = (newTabValue: string) => {
    // Clear preserve state hook on tab change
    clearAllTableStates();
    if (isTagsSearchRoute) {
      setTagsQuotationTab(newTabValue);
      sessionStorage.setItem(tagsSearchQuotationStorageKey, newTabValue);
      return;
    }
    navigate(`/quotation/product/${newTabValue}`);
  };

  React.useEffect(() => {
    if (!isTagsSearchRoute) return;
    const saved = sessionStorage.getItem(tagsSearchQuotationStorageKey);
    const next = saved === "draft" ? "draft" : "log";
    setTagsQuotationTab(next);
  }, [isTagsSearchRoute]);

  React.useEffect(() => {
    return () => {
      if (!window.location.pathname.startsWith("/tags-search")) {
        sessionStorage.removeItem(tagsSearchQuotationStorageKey);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!isTagsSearchRoute || !noWrapper || !tagFromUrl) return;
    let mounted = true;
    setTagsTabSummary((prev) => ({ ...prev, loading: true, hasError: false }));

    Promise.all([
      dispatch(
        fetchAllProductQuotations({
          page: 1,
          pageSize: 1,
          sort: "",
          order: "",
          search: tagFromUrl,
          search_type: "tag",
          filters: [],
          type: QUOTATION_STAGE.LOG,
        })
      ).unwrap(),
      dispatch(
        fetchAllProductQuotations({
          page: 1,
          pageSize: 1,
          sort: "",
          order: "",
          search: tagFromUrl,
          search_type: "tag",
          filters: [],
          type: QUOTATION_STAGE.DRAFT,
        })
      ).unwrap(),
    ])
      .then(([logRes, draftRes]) => {
        if (!mounted) return;
        setTagsTabSummary({
          loading: false,
          logCount: logRes?.rowCount ?? 0,
          draftCount: draftRes?.rowCount ?? 0,
          hasError: false,
        });
      })
      .catch(() => {
        if (!mounted) return;
        setTagsTabSummary({
          loading: false,
          logCount: 0,
          draftCount: 0,
          hasError: true,
        });
      });

    return () => {
      mounted = false;
    };
  }, [dispatch, isTagsSearchRoute, noWrapper, tagFromUrl]);

  const hideEntireTagsSection =
    isTagsSearchRoute &&
    noWrapper &&
    !tagsTabSummary.loading &&
    !tagsTabSummary.hasError &&
    tagsTabSummary.logCount === 0 &&
    tagsTabSummary.draftCount === 0;
  const forceShowOnTagsSearch =
    isTagsSearchRoute && noWrapper && !hideEntireTagsSection;

  const sectionContent = (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h6 className="card-title mb-0">Sales Quotations</h6>
      </div>

      <InternalDynamicTabs
        activeTab={activeTab}
        tabs={tabs}
        onTabChange={handleTabChange}
      />

      <Box sx={{ mt: 3 }}>
        {activeTab === "log" && (
          <QuotationLog
            noWrapper={noWrapper}
            forceShowOnTagsSearch={forceShowOnTagsSearch}
          />
        )}
        {activeTab === "draft" && (
          <QuotationDraft
            noWrapper={noWrapper}
            forceShowOnTagsSearch={forceShowOnTagsSearch}
          />
        )}
      </Box>
    </>
  );

  if (hideEntireTagsSection) {
    return null;
  }

  if (noWrapper) {
    return <div className="mb-4">{sectionContent}</div>;
  }

  return (
    <div className="mb page-content px-4">
      <div className="px-4" style={defaultInnerStyle}>
        {sectionContent}
      </div>
    </div>
  );
};

export default QuotationContainer;
